import { ORDERS_HISTORY_PAGE as dict } from '@/resources/dict'
import { deliveryTypeLabel } from '@/resources/enums/deliveryTypes'
import {
  Filters,
  FilterStore,
  FilterOptionsByType,
  FilterStorePeriod,
} from '@/pages/OrdersHistory/components/OrderHistoryFilters/types'
import { FilterTypes } from '@/pages/OrdersHistory/components/OrderHistoryFilters/enums'
import {
  orderStatusFilterLabel,
  paymentTypeFilterLabel,
  ratingsLabel,
} from '@/resources/constants/ordersHistory'
import { getFormattedDateTimeForUrl } from '@/resources/utils'
import { FilterStoreTypeByProp } from '@/pages/OrdersHistory/components/OrderHistoryFilters/constants'

export const DEFAULT_PAGE_VALUE = 1
export const DEFAULT_PAGE_SIZE_VALUE = 30

const defaultStartPeriod = () => {
  const fourteenDaysAgo = new Date()
  fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14)
  fourteenDaysAgo.setHours(0, 0, 0, 0)
  return fourteenDaysAgo
}

const defaultEndPeriod = () => {
  const today = new Date()
  today.setHours(23, 59, 0, 0)
  return today
}

export const getQueryVariables = () => ({
  pagination: {
    page: DEFAULT_PAGE_VALUE,
    pageSize: DEFAULT_PAGE_SIZE_VALUE,
  },
  period: {
    start: defaultStartPeriod(),
    end: defaultEndPeriod(),
  },
})

const getEntries = (map: { [key: string]: string }) =>
  Object.entries(map).map(([value, label]) => ({
    label,
    value,
  }))

export const filterStatusOptions = getEntries(orderStatusFilterLabel)
export const filterRatingOptions = getEntries(ratingsLabel)
export const filterPaymentTypeOptions = getEntries(paymentTypeFilterLabel)
export const filterDeliveryTypeOptions = getEntries(deliveryTypeLabel)

export const filterOptionsByType: FilterOptionsByType = {
  [FilterTypes.STATUS]: { label: dict.status, options: filterStatusOptions },
  [FilterTypes.RATINGS]: { label: dict.review, options: filterRatingOptions },
  [FilterTypes.PAYMENT_METHODS]: {
    label: dict.payment,
    options: filterPaymentTypeOptions,
  },
  [FilterTypes.DELIVERY_TYPES]: {
    label: dict.deliveryType,
    options: filterDeliveryTypeOptions,
  },
}

export const filterOptionsByStore = (
  type: FilterTypes,
  storeValues: string[]
) => {
  return filterOptionsByType[type].options.filter(({ value }) =>
    storeValues.includes(value)
  )
}

export const getFilterStoreValuesByType = (
  type: FilterTypes,
  filtersStore: FilterStore
) => {
  let values: string[] = []
  switch (type) {
    case FilterTypes.STATUS:
      values = filtersStore.orderStatus
      break
    case FilterTypes.RATINGS:
      values = filtersStore.ratings
      break
    case FilterTypes.PAYMENT_METHODS:
      values = filtersStore.paymentMethods
      break
    case FilterTypes.DELIVERY_TYPES:
      values = filtersStore.deliveryTypes
      break
    default:
      break
  }
  return values
}

export const getSelectedOptionsFromStore = (filtersStore: FilterStore) => {
  const filters = {} as Filters
  Object.keys(filterOptionsByType).forEach(type => {
    const filterType = type as FilterTypes

    filters[filterType] = filterOptionsByStore(
      FilterTypes[filterType],
      getFilterStoreValuesByType(type as FilterTypes, filtersStore)
    )
  })

  return filters
}

export const validateFilterOptions = (
  prop: string,
  value: string[] | string | number
) => {
  const type: FilterTypes = FilterStoreTypeByProp[prop]
  const filter = filterOptionsByType[type]
  if (!filter || typeof value !== 'object') return value

  const filterValues = filter?.options?.map(({ value }) => value)
  return value.filter(v => filterValues.includes(v))
}

export const validatePage = (value?: number) => {
  if (value && !isNaN(value)) return value
  return DEFAULT_PAGE_VALUE
}

export const validatePageSize = (value?: number) => {
  if (value && Object.values(dict.pageSize).includes(value)) return value
  return DEFAULT_PAGE_SIZE_VALUE
}

export const getFormattedPeriodForUrl = (storePeriod: FilterStorePeriod) => [
  getFormattedDateTimeForUrl(new Date(storePeriod.start)),
  getFormattedDateTimeForUrl(new Date(storePeriod.end)),
]

export const getSearchFilter = (value: string) =>
  value ? { key: 'SEARCH_VALUE', value } : undefined
